import { FormControl } from "@atoms"
import { Button, Drawer, DrawerProps, Input, Select } from "antd"
import { Controller, useForm } from "react-hook-form"
import { useApplyTransition, useMemberRespondents } from "./queries"
import { useTranslation } from "react-i18next"
import { object, string } from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import TextArea from "antd/es/input/TextArea"
import { useEffect } from "react"
import { useEventId } from ".."
import { IMember } from "./types"
import moment from "moment"

interface Props extends DrawerProps {
  onClose: () => void
  isOpen: boolean
  data?: {
    transition: string
    presenceId: string
    member: IMember
  }
}

const PresenceDrawer: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const eventId = useEventId()
  const respondents = useMemberRespondents(props.data?.member.id!, {
    enabled: props.isOpen,
  })
  const applyTransition = useApplyTransition(eventId!)
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
  } = usePresenceForm()

  const onSubmit = (data: IPresenceForm) => {
    applyTransition.mutate(
      {
        formData: {
          ...data,
          attendant_at:
            new Date().toISOString().slice(0, 10) + " " + watch("attendant_at"),
          transition: props.data?.transition!,
        },
        presenceId: props.data?.presenceId!,
      },
      {
        onSuccess: props.onClose,
      }
    )
  }

  useEffect(() => {
    props.isOpen &&
      reset({
        attendant_at: moment().format("HH:mm"),
        accompanist: "transport",
      } as any)
  }, [reset, props.isOpen])

  return (
    <Drawer title={props.data?.member.name} {...props}>
      <form>
        <FormControl>
          <Controller
            control={control}
            name="accompanist"
            render={({ field }) => {
              const options = [
                {
                  label: t("labels.transport"),
                  value: "transport",
                },
                {
                  label: t("members.respondent"),
                  value: "respondent",
                },
                {
                  label: t("labels.other", { count: 1 }),
                  value: "other",
                },
              ]

              return (
                <Select
                  options={options}
                  showSearch
                  placeholder={t("global.select", {
                    entity: t("global.item"),
                    context: "male",
                  })}
                  {...field}
                />
              )
            }}
          />
          {errors.accompanist && (
            <FormControl.HelperText>
              {errors.accompanist.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        {watch("accompanist") === "respondent" && (
          <FormControl>
            <Controller
              control={control}
              name="respondent_id"
              render={({ field }) => {
                return (
                  <Select
                    loading={respondents.isLoading}
                    options={respondents.data?.map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))}
                    showSearch
                    placeholder={t("global.select", {
                      entity: t("members.respondent"),
                      context: "male",
                    })}
                    {...field}
                  />
                )
              }}
            />
            {errors.respondent_id && (
              <FormControl.HelperText state="error">
                {errors.respondent_id?.message}
              </FormControl.HelperText>
            )}
          </FormControl>
        )}

        {watch("accompanist") === "other" && (
          <FormControl>
            <Controller
              control={control}
              name="other_note"
              render={({ field }) => (
                <TextArea {...field} placeholder={t("labels.note")} />
              )}
            />

            {errors.other_note && (
              <FormControl.HelperText state="error">
                {errors.other_note?.message}
              </FormControl.HelperText>
            )}
          </FormControl>
        )}

        <FormControl>
          <Controller
            control={control}
            name="attendant_at"
            render={({ field }) => (
              <Input {...field} placeholder={t("labels.time")} type="time" />
            )}
          />

          {errors.attendant_at && (
            <FormControl.HelperText>
              {errors.attendant_at.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <Button
          loading={applyTransition.isLoading}
          type="primary"
          onClick={handleSubmit(onSubmit)}
        >
          {t("global.save")}
        </Button>
      </form>
    </Drawer>
  )
}

export default PresenceDrawer

export interface IPresenceForm {
  attendant_at: string
  accompanist: string
  respondent_id?: string
  transition: string
  other_note?: string
}

export function usePresenceForm(defaultValues?: IPresenceForm) {
  const schema = object({
    attendant_at: string().required(),
    accompanist: string().required(),
    respondent_id: string()
      .nullable()
      .when("accompanist", {
        is: "respondent",
        then: (schema) => schema.required(),
      }),
    other_note: string()
      .nullable()
      .when("accompanist", {
        is: "other",
        then: (schema) => schema.required(),
      }),
  }).required()

  return useForm<IPresenceForm>({
    resolver: yupResolver(schema),
    defaultValues,
  })
}
